import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PINEntry from './PINEntry';
import { useWallet } from '../../contexts/WalletContext';

const getAmountFontSize = (val) => {
  const len = String(val || '').length;
  if (len > 7) return '26px';
  if (len > 5) return '32px';
  if (len > 3) return '38px';
  return '42px';
};

const TopUp = ({ onBack, onSuccess, t, isRTL }) => {
  const [amount, setAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPIN, setShowPIN] = useState(false);
  const { topUp } = useWallet();

  const tr = t || {
    topUpTitle: 'Top Up', addFunds: 'Add Funds', chooseMethod: 'Choose Method',
    bankTransfer: 'Bank Transfer', debitCard: 'Debit / Credit Card',
    cashDeposit: 'Cash Deposit', continueBtn: 'Continue', back: '←',
  };

  const methods = [
    { id: 'bank', methodKey: 'bankTransfer', icon: '🏦', label: tr.bankTransfer, desc: 'Direct from your bank account' },
    { id: 'card', methodKey: 'debitCard',    icon: '💳', label: tr.debitCard,    desc: 'Visa, Mastercard, CMI' },
    { id: 'cash', methodKey: 'cashDeposit',  icon: '💵', label: tr.cashDeposit,  desc: 'At any SIMO agent point' },
  ];

  const handleContinue = () => {
    if (!amount || !selectedMethod || parseFloat(amount) <= 0) return;
    setShowPIN(true);
  };

  const handlePINSuccess = () => {
    setLoading(true);
    setTimeout(() => {
      // Apply the top-up to the wallet (increases balance + adds credit transaction).
      // We pass methodKey so the wallet stores a translation key (not a literal string).
      topUp(amount, selectedMethod.methodKey);
      setLoading(false);
      setShowPIN(false);
      const toastMsg = typeof tr.toastTopUpSuccess === 'function'
        ? tr.toastTopUpSuccess(amount, selectedMethod.label)
        : `Top up of ${amount} MAD via ${selectedMethod.label} successful!`;
      onSuccess(toastMsg);
    }, 1400);
  };

  return (
    <div
      data-testid="topup-screen"
      className="no-scrollbar"
      style={{
        background: '#0F0F0F',
        height: '100%',
        fontFamily: 'Inter, sans-serif',
        overflowY: 'auto',
        paddingBottom: '12px',
        direction: isRTL ? 'rtl' : 'ltr',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '18px 20px 14px' }}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onBack}
          style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#F5F1EA', fontSize: '16px', flexShrink: 0 }}
        >
          {isRTL ? '→' : '←'}
        </motion.button>
        <h2 style={{ color: '#F5F1EA', fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.4px' }}>{tr.topUpTitle}</h2>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Amount section — fixed short golden line, amount centered above with currency on right */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
          <p style={{ color: '#8B93A7', fontSize: '11px', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>{tr.addFunds}</p>

          <label
            htmlFor="topup-amount-input"
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: '10px',
              height: '56px',
              cursor: 'text',
              width: '100%',
              maxWidth: '280px',
            }}
          >
            <input
              id="topup-amount-input"
              data-testid="topup-amount-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                if (val.length <= 7) setAmount(val);
              }}
              placeholder="0"
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                color: amount ? '#F5F1EA' : '#5C6478',
                fontSize: getAmountFontSize(amount || '0'),
                fontWeight: '800',
                letterSpacing: '-1.5px',
                textAlign: 'right',
                width: 'auto',
                minWidth: '40px',
                maxWidth: '180px',
                fontFamily: 'Inter, sans-serif',
                transition: 'font-size 0.15s ease, color 0.2s ease',
                padding: 0,
              }}
              size={Math.max(1, (amount || '0').length)}
            />
            <span style={{ color: '#8B93A7', fontSize: '17px', fontWeight: '500', flexShrink: 0 }}>MAD</span>
          </label>

          {/* Constant short golden line */}
          <div
            style={{
              width: '88px',
              height: '2px',
              background: 'linear-gradient(90deg, rgba(212,175,55,0.15), rgba(212,175,55,0.85), rgba(212,175,55,0.15))',
              borderRadius: '2px',
              marginTop: '6px',
              boxShadow: '0 0 12px rgba(212,175,55,0.35)',
            }}
          />
        </div>

        {/* Quick amounts */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '28px', flexWrap: 'wrap' }}>
          {[100, 250, 500, 1000].map((val) => (
            <motion.button
              key={val}
              whileTap={{ scale: 0.93 }}
              onClick={() => setAmount(String(val))}
              style={{ padding: '6px 14px', borderRadius: '100px', background: amount === String(val) ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.04)', border: amount === String(val) ? '1px solid rgba(212,175,55,0.25)' : '1px solid rgba(255,255,255,0.07)', color: amount === String(val) ? '#D4AF37' : '#8B93A7', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
            >
              {val}
            </motion.button>
          ))}
        </div>

        {/* Method selection */}
        <p style={{ color: '#8B93A7', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>{tr.chooseMethod}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
          {methods.map((method) => (
            <motion.div
              key={method.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedMethod(method)}
              data-testid={`method-${method.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: selectedMethod?.id === method.id ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.03)', border: selectedMethod?.id === method.id ? '1px solid rgba(212,175,55,0.2)' : '1px solid rgba(255,255,255,0.07)', borderRadius: '14px', cursor: 'pointer', transition: 'all 0.2s' }}
            >
              <div style={{ width: '40px', height: '40px', borderRadius: '13px', background: selectedMethod?.id === method.id ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{method.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: '#F5F1EA', fontSize: '13px', fontWeight: '600', margin: 0 }}>{method.label}</p>
                <p style={{ color: '#8B93A7', fontSize: '11px', margin: 0 }}>{method.desc}</p>
              </div>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: selectedMethod?.id === method.id ? '5px solid #D4AF37' : '2px solid rgba(255,255,255,0.15)', transition: 'all 0.2s', flexShrink: 0 }} />
            </motion.div>
          ))}
        </div>

        <motion.button
          data-testid="topup-continue-btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleContinue}
          disabled={!amount || !selectedMethod || loading}
          style={{ width: '100%', background: amount && selectedMethod ? 'linear-gradient(135deg, #D4AF37, #B8962E)' : 'rgba(255,255,255,0.06)', color: amount && selectedMethod ? '#0F0F0F' : '#8B93A7', border: 'none', borderRadius: '16px', padding: '15px', fontSize: '15px', fontWeight: '700', cursor: amount && selectedMethod ? 'pointer' : 'not-allowed', transition: 'all 0.25s' }}
        >
          {loading ? 'Processing...' : tr.continueBtn}
        </motion.button>
      </div>

      {/* PIN Entry — full screen change */}
      {showPIN && (
        <PINEntry
          amount={amount}
          recipient={selectedMethod?.label}
          mode="topup"
          onSuccess={handlePINSuccess}
          onCancel={() => setShowPIN(false)}
          t={t}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default TopUp;
